import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Spinner } from '@picsio/ui';
import cn from 'classnames';

import { listProductTypes, renameProductType } from '../../../store/productTypes/actions';
import localization from '../../../shared/strings';
import Toast from '../../Toast/index';
import SearchBar from '../../SearchBar/SearchBar';
import ProductTypeRow from './ProductTypeRow';
import { filterProductTypesByQuery, findProductTypeByNormalizedTitle } from '../ProductType/productTypesDropdown/helpers';
import { getTrimmedTitleIfChanged } from './helpers';
import Icon from '../../Icon';
import styles from './styles.module.scss';

const l10n = localization.PRODUCT_INFO.productType;

const ProductTypesManagementDialog = ({ onClose }) => {
  const dispatch = useDispatch();
  const items = useSelector((state) => state.productTypes.items);
  const isLoading = useSelector((state) => state.productTypes.isLoading);
  const isLoaded = useSelector((state) => state.productTypes.isLoaded);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editingOriginalTitle, setEditingOriginalTitle] = useState('');
  const [hasRenameError, setHasRenameError] = useState(false);

  useEffect(() => {
    if (!isLoaded && !isLoading) dispatch(listProductTypes());
  }, [dispatch, isLoaded, isLoading]);

  const filteredItems = useMemo(
    () => filterProductTypesByQuery(items, searchQuery),
    [items, searchQuery],
  );

  const handleStartRename = useCallback((item) => {
    setEditingId(item._id);
    setEditValue(item.title);
    setEditingOriginalTitle(item.title);
    setHasRenameError(false);
  }, []);

  const handleChangeEditValue = useCallback((e) => {
    setEditValue(e.target.value);
    setHasRenameError(false);
  }, []);

  const handleCancelRename = useCallback(() => {
    setEditingId(null);
    setHasRenameError(false);
  }, []);

  const commitRename = useCallback(async () => {
    if (!editingId) return;
    const productTypeId = editingId;
    const originalTitle = editingOriginalTitle;
    const pendingEditValue = editValue;
    const titleToRename = getTrimmedTitleIfChanged(pendingEditValue, originalTitle);
    if (!titleToRename) {
      setEditingId(null);
      return;
    }

    const duplicateProductType = findProductTypeByNormalizedTitle(items, titleToRename);
    if (duplicateProductType && duplicateProductType._id !== productTypeId) {
      Toast(localization.PRODUCT_TYPES.alreadyExists, { autoClose: 5000, type: 'error' });
      setHasRenameError(true);
      return;
    }

    setEditingId(null);
    const result = await dispatch(renameProductType({
      productTypeId, title: titleToRename, originalTitle,
    }));
    if (renameProductType.fulfilled.match(result) && result.payload?.cancelled) {
      setEditingId(productTypeId);
      setEditValue(pendingEditValue);
      return;
    }
    if (renameProductType.fulfilled.match(result) && result.payload === null) {
      setEditingId(productTypeId);
      setEditValue(pendingEditValue);
      setHasRenameError(true);
      return;
    }
    /** the "already exists" case is already surfaced by the thunk itself via its own Toast */
    if (renameProductType.rejected.match(result)) {
      Toast(l10n.renameFailed, { autoClose: false, type: 'error' });
    }
  }, [dispatch, editingId, editingOriginalTitle, editValue, items]);

  const handleSave = useCallback(async () => {
    await commitRename();
    onClose();
  }, [commitRename, onClose]);

  return (
    <div className={cn('simpleDialog', styles.simpleDialogWrapper)}>
      <div className="simpleDialogUnderlayer" />
      <div className="simpleDialogBox">
        <div className="simpleDialogHeader">
          <span className="simpleDialogTitle">{l10n.manageTitle}</span>
          <span
            role="button"
            tabIndex={0}
            className="simpleDialogBtnCross"
            onClick={onClose}
            onKeyDown={onClose}
          >
            <Icon name="close" />
          </span>
        </div>
        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <SearchBar
              className={styles.searchBar}
              placeholder={l10n.searchPlaceholder}
              applySearch={setSearchQuery}
            />
            <Choose>
              <When condition={!isLoaded}>
                <Spinner text={l10n.loadingProductTypes} />
              </When>
              <When condition={items.length === 0}>
                <p className={styles.emptyState}>{l10n.emptyList}</p>
              </When>
              <When condition={filteredItems.length === 0}>
                <p className={styles.emptyState}>{l10n.emptySearchList}</p>
              </When>
              <Otherwise>
                <ul className={styles.list}>
                  {filteredItems.map((item) => (
                    <ProductTypeRow
                      key={item._id}
                      item={item}
                      isEditing={item._id === editingId}
                      editValue={editValue}
                      hasEditError={hasRenameError}
                      onStartRename={handleStartRename}
                      onChangeEditValue={handleChangeEditValue}
                      onCommitRename={commitRename}
                      onCancelRename={handleCancelRename}
                    />
                  ))}
                </ul>
              </Otherwise>
            </Choose>
          </div>
        </div>
        <div className="simpleDialogFooter">
          <Button color="secondary" size="md" variant="contained" onClick={onClose}>
            {l10n.cancel}
          </Button>
          <Button color="primary" size="md" variant="contained" onClick={handleSave}>
            {l10n.save}
          </Button>
        </div>
      </div>
    </div>
  );
};

ProductTypesManagementDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default ProductTypesManagementDialog;
